import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import assignProps from "../assignProps.mjs";
export var enforcedKeyboardFocusEnhanceContext = /*#__PURE__*/React.createContext(false);
var lastFocusSource = 'none';
var focusSourceListeners = [];
export var useFocusSource = function useFocusSource() {
  var handleMouseDown = React.useCallback(function () {
    return focusSourceListeners.forEach(function (listener) {
      return listener.setFocusSource('mouse');
    });
  }, []);
  var handleKeyDown = React.useCallback(function () {
    return focusSourceListeners.forEach(function (listener) {
      return listener.setFocusSource('keyboard');
    });
  }, []);
  var focusSourceRef = React.useRef(lastFocusSource);
  var setFocusSource = React.useCallback(function (source) {
    focusSourceRef.current = source;
    lastFocusSource = source;
  }, []);
  var subscribeListeners = React.useCallback(function () {
    document.addEventListener('mousedown', handleMouseDown, {
      capture: true
    });
    document.addEventListener('keydown', handleKeyDown, {
      capture: true
    });
  }, []);
  var unsubscribeListeners = React.useCallback(function () {
    document.removeEventListener('mousedown', handleMouseDown, {
      capture: true
    });
    document.removeEventListener('keydown', handleKeyDown, {
      capture: true
    });
  }, []);
  React.useEffect(function () {
    var needToAddListeners = focusSourceListeners.length === 0;
    var sourceListener = {
      setFocusSource: setFocusSource,
      subscribeListeners: subscribeListeners
    };
    focusSourceListeners.push(sourceListener);
    if (needToAddListeners) subscribeListeners();
    var needToRemoveListeners = needToAddListeners;
    return function () {
      var sourceListenerIndex = focusSourceListeners.indexOf(sourceListener);
      focusSourceListeners.splice(sourceListenerIndex, 1);
      if (needToRemoveListeners) unsubscribeListeners();
      if (needToRemoveListeners && focusSourceListeners.length > 0) {
        focusSourceListeners[0].subscribeListeners();
      }
    };
  }, []);
  return focusSourceRef;
};
var keyboardFocusEnhance = function keyboardFocusEnhance() {
  var makeFocusable = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  return function (props) {
    var _props$tabIndex;
    var autoFocus = props.autoFocus;
    var disabled = props.disabled || props.loading;
    var tabIndex = (_props$tabIndex = props.tabIndex) !== null && _props$tabIndex !== void 0 ? _props$tabIndex : makeFocusable ? 0 : undefined;
    var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      keyboardFocused = _React$useState2[0],
      setKeyboardFocused = _React$useState2[1];
    var focusSourceRef = useFocusSource();
    var ref = React.useRef(null);
    var handleFocus = React.useCallback(function (event) {
      if (event.isTrusted === true) {
        if (focusSourceRef.current !== 'keyboard') return;
      }
      setKeyboardFocused(true);
    }, []);
    var handlerBlur = React.useCallback(function () {
      return setKeyboardFocused(false);
    }, []);
    React.useEffect(function () {
      if (typeof autoFocus !== 'number' && !autoFocus) return;
      var timer = setTimeout(function () {
        var _ref$current;
        (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.focus();
        setKeyboardFocused(true);
      }, 0);
      return function () {
        clearTimeout(timer);
      };
    }, [autoFocus]);
    React.useEffect(function () {
      if (disabled) {
        setKeyboardFocused(false);
      }
    }, [disabled]);
    var enforcedKeyboardFocus = React.useContext(enforcedKeyboardFocusEnhanceContext);
    return assignProps(props, {
      tabIndex: disabled ? -1 : tabIndex,
      keyboardFocused: (keyboardFocused || enforcedKeyboardFocus) && !disabled,
      onFocus: handleFocus,
      onBlur: handlerBlur,
      ref: ref
    });
  };
};
export default keyboardFocusEnhance;
//# sourceMappingURL=keyboardFocusEnhance.mjs.map